import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
import "./Gallery.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Icon from "../../components/Icon";

// Helper
import { apiFetch, resolveTenant, cachedTenant } from "../../Helper/Api";
import { brandingOf } from "../../Helper/Branding";
import { t } from "../../Helper/i18n";

// Properties
const properties = require("../../properties.json");

class Gallery extends Component {
  state = {
    tenant: cachedTenant(),
    notFound: false,
    projects: [],
    loading: true,
    openIndex: null,
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        document.title = tenant.name ? `${t("gallery")} | ${tenant.name}` : properties.site_name;
        this.setState({ tenant });
        return apiFetch("/gallery-projects", { query: { branchId: tenant.branchId } });
      })
      .then((projects) => this.setState({ projects: projects || [], loading: false }))
      .catch(() => this.setState({ notFound: true, loading: false }));

    document.addEventListener("keydown", this.onKey);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKey);
    if (this.io) this.io.disconnect();
  }

  // Escape closes the project, and the arrows walk between them -- the old
  // overlay could only be dismissed by clicking the backdrop or the ✕.
  onKey = (e) => {
    const { openIndex, projects } = this.state;
    if (openIndex === null) return;
    if (e.key === "Escape") this.closeModal();
    if (e.key === "ArrowRight") this.setState({ openIndex: (openIndex + 1) % projects.length });
    if (e.key === "ArrowLeft") this.setState({ openIndex: (openIndex - 1 + projects.length) % projects.length });
  };

  /* Reveal as tiles are reached. Nothing hides until this has run, so a page
     whose script never arrives still shows the whole gallery. */
  watchReveal = (root) => {
    if (!root || this.io) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;

    root.classList.add("js-reveal");
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          this.io.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );
    root.querySelectorAll("[data-reveal]").forEach((el) => this.io.observe(el));
  };

  openProject = (index) => this.setState({ openIndex: index });
  closeModal = () => this.setState({ openIndex: null });

  render() {
    const { tenant, notFound, projects, loading, openIndex } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const teacherName = tenant.name || "";
    const b = brandingOf(tenant);
    const project = openIndex !== null ? projects[openIndex] : null;

    return (
      <>
        <div className="gx-stage mu mu-ground">
          <div className="gx-sky" aria-hidden="true">
            <span className="aur a1"></span>
            <span className="aur a2"></span>
            <span className="dots"></span>
          </div>

          <Header tenant={tenant} onStage />

          <div className="gx-head">
            <span className="mu-chip"><Icon name="image" className="chipi" /> {t("ourWork")}</span>
            <h1>{t("gallery")}</h1>
            <p>{b.tagline || t("galleryLead")}</p>
            {!loading && projects.length > 0 && (
              <span className="gx-count">
                {projects.length === 1 ? t("projectsOne") : t("projectsCount", { n: projects.length })}
              </span>
            )}
          </div>
        </div>

        <div className="gx-body mu">
          {loading ? (
            <div className="gx-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span className="gx-tile is-wait" key={i} aria-hidden="true"></span>
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="gx-empty">
              <span className="ic" aria-hidden="true"><Icon name="image" /></span>
              <b>{t("nothingHereYet")}</b>
              <p>{t("noProjectsText")}</p>
              <Link to="/contact" className="mu-btn is-gold has-arw">
                {t("contactName", { name: teacherName })} <Icon name="arrow" className="arw" />
              </Link>
            </div>
          ) : (
            <div className="gx-grid" ref={this.watchReveal}>
              {projects.map((proj, i) => {
                const cover = proj.photos && proj.photos[0];
                const shots = (proj.photos || []).length;
                return (
                  <button
                    type="button"
                    className={`gx-tile ${cover ? "" : "is-blank"}`}
                    key={proj.id}
                    data-reveal
                    style={{ "--i": i % 3, ...(cover ? { backgroundImage: `url(${cover})` } : {}) }}
                    onClick={() => this.openProject(i)}
                  >
                    {!cover && <span className="mark" aria-hidden="true"><Icon name="image" /></span>}

                    <span className="gx-badges">
                      {shots > 0 && (
                        <span className="gx-badge"><Icon name="image" /> {shots}</span>
                      )}
                      {proj.videoUrl && (
                        <span className="gx-badge"><Icon name="play" /> {t("video")}</span>
                      )}
                    </span>

                    <span className="gx-info">
                      {proj.tag && <span className="gx-tag">{proj.tag.split(" · ")[0]}</span>}
                      <strong>{proj.title}</strong>
                    </span>

                    <span className="gx-open" aria-hidden="true"><Icon name="arrow" /></span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* the project, opened over the page */}
        <div
          className={`gx-overlay mu ${project ? "is-open" : ""}`}
          onClick={(e) => e.target === e.currentTarget && this.closeModal()}
          role={project ? "dialog" : undefined}
          aria-modal={project ? "true" : undefined}
          aria-label={project ? project.title : undefined}
        >
          {project && (
            <div className="gx-box">
              <div className="gx-boxhead">
                <div className="gx-boxtitle">
                  <h2>{project.title}</h2>
                  {project.tag && <span className="gx-boxtag">{project.tag}</span>}
                </div>
                <span className="gx-counter">{openIndex + 1} / {projects.length}</span>
                <button className="gx-close" onClick={this.closeModal} aria-label={t("close")}>
                  <Icon name="arrow" />
                </button>
              </div>

              <div className="gx-boxbody">
                {(project.photos || []).length > 0 && (
                  <div className="gx-shots">
                    {(project.photos || []).map((url, k) => (
                      <a
                        className="gx-shot"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={k}
                        style={{ backgroundImage: `url(${url})`, "--i": k }}
                        aria-label={t("photoN", { n: k + 1 })}
                      ></a>
                    ))}
                  </div>
                )}

                {project.videoUrl && (
                  <div className="gx-video">
                    <span className="gx-vidlabel"><Icon name="play" /> {t("projectVideo")}</span>
                    <video src={project.videoUrl} controls />
                  </div>
                )}
              </div>

              {projects.length > 1 && (
                <div className="gx-boxfoot">
                  <button
                    type="button"
                    className="mu-btn is-onteal is-sm"
                    onClick={() => this.setState({ openIndex: (openIndex - 1 + projects.length) % projects.length })}
                  >
                    <Icon name="arrow" className="back" /> {t("prev")}
                  </button>
                  <button
                    type="button"
                    className="mu-btn is-onteal is-sm"
                    onClick={() => this.setState({ openIndex: (openIndex + 1) % projects.length })}
                  >
                    {t("next")} <Icon name="arrow" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Gallery;
